/**
 * Budget vs Actual — Current Budget vs committed/actual ledgers, exceedances, source drill-down.
 */
import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Drawer,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, PrinterOutlined, ReloadOutlined } from '@ant-design/icons';
import { useProject } from '@/hooks/useProjects';
import { projectService } from '@/services/project.service';
import { formatMoney } from '@/utils/format';
import { EngineReportPrint } from '@/components/print/EngineCommercialPrints';

const { Title, Text } = Typography;

function statusTag(status: unknown) {
  const s = String(status || 'ok');
  if (s === 'over') return <Tag color="error">Over</Tag>;
  if (s === 'tight') return <Tag color="warning">Tight</Tag>;
  return <Tag color="success">OK</Tag>;
}

function exceedanceStatusTag(status: unknown) {
  const s = String(status || '');
  if (s === 'Consumed') return <Tag color="blue">Consumed</Tag>;
  if (s === 'Approved') return <Tag color="success">Approved</Tag>;
  if (s === 'Submitted') return <Tag color="processing">Pending</Tag>;
  return <Tag>{s || '—'}</Tag>;
}

function sourceDocPath(type: string, id: string): string | null {
  switch (String(type || '').toLowerCase()) {
    case 'purchase_order':
      return `/scm/procurement/purchase-orders/${id}`;
    case 'supplier_invoice':
      return `/scm/procurement/supplier-invoices/${id}`;
    case 'purchase_requisition':
      return `/scm/procurement/purchase-requests/${id}`;
    default:
      return null;
  }
}

export const ProjectBudgetVsActualPage: React.FC = () => {
  const { id: projectId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: project } = useProject(projectId);
  const [groupBy, setGroupBy] = useState<'line' | 'wbs' | 'cbs' | 'category'>('line');
  const [overOnly, setOverOnly] = useState(false);
  const [drill, setDrill] = useState<Record<string, unknown> | null>(null);

  const q = useQuery({
    queryKey: ['budget-vs-actual', projectId, groupBy],
    queryFn: () => projectService.getBudgetVsActual(projectId, groupBy),
    enabled: !!projectId,
  });

  const eventsQ = useQuery({
    queryKey: [
      'cost-control-events',
      projectId,
      drill?.wbsNodeId,
      drill?.costCategory,
      drill?.cbsCodeId,
    ],
    queryFn: () =>
      projectService.listCostControlEvents(projectId, {
        wbsNodeId: (drill?.wbsNodeId as string) || null,
        costCategory: (drill?.costCategory as string) || null,
        cbsCodeId: (drill?.cbsCodeId as string) || null,
        limit: 100,
      }),
    enabled: !!projectId && !!drill,
  });

  const allRows = ((q.data as any)?.rows || []) as Record<string, unknown>[];
  const rows = useMemo(
    () => (overOnly ? allRows.filter((r) => String(r.status) === 'over' || Number(r.available) < 0) : allRows),
    [allRows, overOnly]
  );
  const summary = (q.data as any)?.summary as Record<string, unknown> | null;
  const active = !!(q.data as any)?.active;
  const exceedances = ((q.data as any)?.exceedances || []) as Record<string, unknown>[];
  const unmatched = ((q.data as any)?.unmatchedCommitments || []) as Record<string, unknown>[];

  const projectNumber = String((project as any)?.project_number || '');
  const projectName = String((project as any)?.project_name || '');

  const handlePrint = () => {
    const mainContent = document.getElementById('root');
    if (mainContent) mainContent.style.display = 'none';
    const printContainer = document.createElement('div');
    printContainer.id = 'print-container';
    printContainer.style.display = 'block';
    document.body.appendChild(printContainer);
    const root = ReactDOM.createRoot(printContainer);
    const cleanup = () => {
      root.unmount();
      if (document.body.contains(printContainer)) document.body.removeChild(printContainer);
      if (mainContent) mainContent.style.display = 'block';
      window.removeEventListener('afterprint', cleanup);
    };

    const columns =
      groupBy === 'line'
        ? [
            { key: 'wbs', label: 'WBS', width: '16%' },
            { key: 'cbs', label: 'CBS', width: '8%' },
            { key: 'category', label: 'Category', width: '10%' },
            { key: 'budgeted', label: 'Budget', width: '11%', align: 'right' as const },
            { key: 'committed', label: 'Committed', width: '11%', align: 'right' as const },
            { key: 'actual', label: 'Actual', width: '11%', align: 'right' as const },
            { key: 'available', label: 'Available', width: '11%', align: 'right' as const },
            { key: 'forecast', label: 'Forecast', width: '11%', align: 'right' as const },
            { key: 'status', label: 'Status', width: '10%' },
          ]
        : [
            {
              key: 'label',
              label: groupBy === 'wbs' ? 'WBS' : groupBy === 'cbs' ? 'CBS' : 'Category',
              width: '24%',
            },
            { key: 'budgeted', label: 'Budget', width: '14%', align: 'right' as const },
            { key: 'committed', label: 'Committed', width: '14%', align: 'right' as const },
            { key: 'actual', label: 'Actual', width: '14%', align: 'right' as const },
            { key: 'available', label: 'Available', width: '14%', align: 'right' as const },
            { key: 'forecast', label: 'Forecast', width: '14%', align: 'right' as const },
            { key: 'status', label: 'Status', width: '10%' },
          ];

    const lines = rows.map((r, idx) => ({
      id: String(r.id || r.key || idx),
      lineNumber: idx + 1,
      description:
        groupBy === 'line'
          ? `${r.wbsCode || '—'} · ${r.costCategory || ''}`
          : String(r.label || '—'),
      cells: {
        wbs: r.wbsCode ? `${r.wbsCode} — ${r.wbsTitle || ''}` : '—',
        cbs: String(r.cbsCode || '—'),
        category: String(r.costCategory || '—'),
        label: String(r.label || '—'),
        budgeted: formatMoney(Number(r.budgeted) || 0, { display: 'plain' }),
        committed: formatMoney(Number(r.committed) || 0, { display: 'plain' }),
        actual: formatMoney(Number(r.actual) || 0, { display: 'plain' }),
        available: formatMoney(Number(r.available) || 0, { display: 'plain' }),
        forecast: formatMoney(Number(r.forecast) || 0, { display: 'plain' }),
        status: String(r.status || 'ok'),
      },
    }));

    root.render(
      <EngineReportPrint
        title="Budget vs Actual"
        documentNumber={`${projectNumber || projectId}-BVA`}
        partyName={projectName || projectNumber}
        metaLines={[
          { label: 'Project', value: `${projectNumber} · ${projectName}` },
          { label: 'Group by', value: groupBy },
          ...(summary
            ? [
                {
                  label: 'Budget',
                  value: formatMoney(Number(summary.budgeted) || 0, { display: 'plain' }),
                },
                {
                  label: 'Committed',
                  value: formatMoney(Number(summary.committed) || 0, { display: 'plain' }),
                },
                {
                  label: 'Actual',
                  value: formatMoney(Number(summary.actual) || 0, { display: 'plain' }),
                },
                {
                  label: 'Available',
                  value: formatMoney(Number(summary.available) || 0, { display: 'plain' }),
                },
                {
                  label: 'Authorized exceedance',
                  value: formatMoney(Number(summary.authorizedOverspend) || 0, { display: 'plain' }),
                },
                {
                  label: 'Position',
                  value: formatMoney(Number(summary.position) || 0, { display: 'plain' }),
                },
              ]
            : []),
        ]}
        columns={columns}
        lines={lines}
        onClose={cleanup}
      />
    );
    window.addEventListener('afterprint', cleanup);
    setTimeout(() => window.print(), 250);
  };

  const commitments = ((eventsQ.data as any)?.commitments || []) as Record<string, unknown>[];
  const actuals = ((eventsQ.data as any)?.actuals || []) as Record<string, unknown>[];

  const authorized = Number(summary?.authorizedOverspend) || 0;
  const available = Number(summary?.available) || 0;
  const position = Number(summary?.position) || 0;
  const committed = Number(summary?.committed) || 0;

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space wrap>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(`/prj/projects/${projectId}/budgets`)}
            >
              Workbench
            </Button>
            <Title level={3} style={{ margin: 0 }}>
              Budget vs Actual
            </Title>
          </Space>
          <Space>
            <Text type="secondary">
              {projectNumber} · {projectName}
            </Text>
            <Button icon={<PrinterOutlined />} onClick={handlePrint} disabled={!rows.length}>
              Print
            </Button>
          </Space>
        </Space>

        {!q.isLoading && !active && (
          <Alert
            type="info"
            showIcon
            message="Cost control inactive"
            description="Approve an Original or Current Project Cost Budget to enable Budget vs Actual."
          />
        )}

        {summary && active && (
          <Card size="small">
            <Row gutter={[16, 8]}>
              <Col xs={12} sm={8} md={4} lg={3}>
                <Statistic
                  title="Budget"
                  value={Number(summary.budgeted) || 0}
                  formatter={(v) => formatMoney(Number(v))}
                />
              </Col>
              <Col xs={12} sm={8} md={4} lg={3}>
                <Statistic
                  title="Committed"
                  value={committed}
                  valueStyle={committed > 0 ? { color: '#1677ff' } : undefined}
                  formatter={(v) => formatMoney(Number(v))}
                />
              </Col>
              <Col xs={12} sm={8} md={4} lg={3}>
                <Statistic
                  title="Actual"
                  value={Number(summary.actual) || 0}
                  formatter={(v) => formatMoney(Number(v))}
                />
              </Col>
              <Col xs={12} sm={8} md={4} lg={3}>
                <Statistic
                  title="Available"
                  value={available}
                  valueStyle={available < 0 ? { color: '#cf1322' } : undefined}
                  formatter={(v) => formatMoney(Number(v))}
                />
              </Col>
              <Col xs={12} sm={8} md={4} lg={3}>
                <Statistic
                  title="Authorized excess"
                  value={authorized}
                  valueStyle={authorized > 0 ? { color: '#389e0d' } : undefined}
                  formatter={(v) => formatMoney(Number(v))}
                />
              </Col>
              <Col xs={12} sm={8} md={4} lg={3}>
                <Statistic
                  title="Position"
                  value={position}
                  valueStyle={
                    position < 0 ? { color: '#cf1322' } : position === 0 ? { color: '#389e0d' } : undefined
                  }
                  formatter={(v) => formatMoney(Number(v))}
                />
              </Col>
              <Col xs={12} sm={8} md={4} lg={3}>
                <Statistic
                  title="Forecast"
                  value={Number(summary.forecast) || 0}
                  formatter={(v) => formatMoney(Number(v))}
                />
              </Col>
              <Col xs={12} sm={8} md={4} lg={3}>
                <Statistic
                  title="Lines over"
                  value={Number(summary.linesOver) || 0}
                  valueStyle={Number(summary.linesOver) > 0 ? { color: '#cf1322' } : undefined}
                />
              </Col>
            </Row>
            <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
              Exceedance authorizes overspend — it does not increase Current Budget. Position =
              Available + Authorized excess.
            </Text>
          </Card>
        )}

        {active && authorized > 0 && available < 0 && (
          <Alert
            type="success"
            showIcon
            message="Overspend authorized by Budget Exceedance"
            description={`Raw available is ${formatMoney(available)}; approved/consumed exceedance covers ${formatMoney(authorized)}. Net position ${formatMoney(position)}.`}
          />
        )}

        {active && unmatched.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message={`${unmatched.length} unmatched commitment(s)`}
            description="Spend is coded to WBS/category with no matching budget line — it will not roll into line committed amounts until remapped."
          />
        )}

        {active && exceedances.length > 0 && (
          <Card size="small" title="Budget exceedances">
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={exceedances}
              columns={[
                {
                  title: 'Status',
                  dataIndex: 'status',
                  width: 110,
                  render: (v: string) => exceedanceStatusTag(v),
                },
                {
                  title: 'Over',
                  dataIndex: 'amountOver',
                  align: 'right' as const,
                  width: 120,
                  render: (v: number) => formatMoney(Number(v) || 0),
                },
                {
                  title: 'Reason',
                  dataIndex: 'reason',
                  ellipsis: true,
                  render: (v: unknown) => (v ? String(v) : '—'),
                },
                {
                  title: 'Source',
                  key: 'source',
                  render: (_: unknown, r: Record<string, unknown>) => {
                    const type = String(r.sourceEntityType || '');
                    const id = String(r.sourceEntityId || '');
                    const path = sourceDocPath(type, id);
                    const label = type.replace(/_/g, ' ') || '—';
                    return path ? <Link to={path}>{label}</Link> : label;
                  },
                },
                {
                  title: '',
                  key: 'open',
                  width: 100,
                  render: (_: unknown, r: Record<string, unknown>) => (
                    <Link to={`/prj/budget-exceedances/${encodeURIComponent(String(r.id))}`}>
                      Open
                    </Link>
                  ),
                },
              ]}
            />
          </Card>
        )}

        <Card
          size="small"
          title="Detail"
          extra={
            <Space wrap>
              <Space size={6}>
                <Switch checked={overOnly} onChange={setOverOnly} size="small" />
                <Text type="secondary">Over only</Text>
              </Space>
              <Select
                value={groupBy}
                style={{ width: 140 }}
                onChange={setGroupBy}
                options={[
                  { value: 'line', label: 'By line' },
                  { value: 'wbs', label: 'By WBS' },
                  { value: 'cbs', label: 'By CBS' },
                  { value: 'category', label: 'By category' },
                ]}
              />
              <Button icon={<ReloadOutlined />} onClick={() => q.refetch()} loading={q.isFetching} />
            </Space>
          }
        >
          <Table
            size="small"
            rowKey={(r) => String(r.id || r.key || r.label)}
            loading={q.isLoading}
            dataSource={rows}
            scroll={{ x: 1200 }}
            pagination={{ pageSize: 50 }}
            onRow={(r) => ({
              onClick: () => {
                if (groupBy !== 'line') return;
                setDrill(r);
              },
              style: groupBy === 'line' ? { cursor: 'pointer' } : undefined,
            })}
            columns={[
              ...(groupBy === 'line'
                ? [
                    {
                      title: 'WBS',
                      key: 'wbs',
                      render: (_: unknown, r: Record<string, unknown>) =>
                        r.wbsCode ? `${r.wbsCode} — ${r.wbsTitle || ''}` : '—',
                    },
                    {
                      title: 'CBS',
                      key: 'cbs',
                      width: 90,
                      render: (_: unknown, r: Record<string, unknown>) =>
                        r.cbsCode ? `${r.cbsCode}` : '—',
                    },
                    {
                      title: 'Category',
                      dataIndex: 'costCategory',
                      width: 110,
                      render: (v: string) => (v ? <Tag>{v}</Tag> : '—'),
                    },
                  ]
                : [
                    {
                      title: groupBy === 'wbs' ? 'WBS' : groupBy === 'cbs' ? 'CBS' : 'Category',
                      dataIndex: 'label',
                    },
                  ]),
              {
                title: 'Budget',
                dataIndex: 'budgeted',
                align: 'right' as const,
                render: (v: number) => formatMoney(Number(v) || 0),
              },
              {
                title: 'Committed',
                dataIndex: 'committed',
                align: 'right' as const,
                render: (v: number) => (
                  <Text strong={Number(v) > 0}>{formatMoney(Number(v) || 0)}</Text>
                ),
              },
              {
                title: 'Actual',
                dataIndex: 'actual',
                align: 'right' as const,
                render: (v: number) => formatMoney(Number(v) || 0),
              },
              {
                title: 'Available',
                dataIndex: 'available',
                align: 'right' as const,
                render: (v: number) => (
                  <Text type={Number(v) < 0 ? 'danger' : undefined}>
                    {formatMoney(Number(v) || 0)}
                  </Text>
                ),
              },
              {
                title: 'Forecast',
                dataIndex: 'forecast',
                align: 'right' as const,
                render: (v: number) => formatMoney(Number(v) || 0),
              },
              {
                title: 'Var type',
                dataIndex: 'varianceType',
                width: 90,
                render: (v: string) => {
                  const t = String(v || 'total');
                  if (t === 'rate') return <Tag color="orange">Rate</Tag>;
                  if (t === 'qty') return <Tag color="blue">Qty</Tag>;
                  if (t === 'scope') return <Tag color="purple">Scope</Tag>;
                  return <Tag>Total</Tag>;
                },
              },
              {
                title: 'Util %',
                dataIndex: 'utilizationPct',
                align: 'right' as const,
                width: 80,
                render: (v: number) => `${Number(v) || 0}%`,
              },
              {
                title: 'Status',
                dataIndex: 'status',
                width: 90,
                render: (v: string) => statusTag(v),
              },
            ]}
          />
          {groupBy === 'line' && (
            <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              Click a line to drill into commitment / actual source documents (e.g. PO-2026-0001).
            </Text>
          )}
        </Card>
      </Space>

      <Drawer title="Source events" open={!!drill} onClose={() => setDrill(null)} width={640}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Text>
            {String(drill?.wbsCode || '—')} · {String(drill?.costCategory || '')}
            {Number(drill?.committed) > 0
              ? ` · Committed ${formatMoney(Number(drill?.committed) || 0)}`
              : ''}
          </Text>
          <Card size="small" title="Commitments" loading={eventsQ.isLoading}>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={commitments}
              locale={{ emptyText: 'No commitment events for this line' }}
              columns={[
                {
                  title: 'Document',
                  key: 'doc',
                  render: (_: unknown, r: Record<string, unknown>) => {
                    const path = (r.document_path as string) || null;
                    const label = String(r.document_number || r.source_label || r.source_type || '—');
                    return path ? <Link to={path}>{label}</Link> : label;
                  },
                },
                {
                  title: 'Amount',
                  dataIndex: 'amount',
                  align: 'right' as const,
                  width: 120,
                  render: (v: number) => formatMoney(Number(v) || 0),
                },
                {
                  title: 'Kind',
                  dataIndex: 'event_kind',
                  width: 140,
                  ellipsis: true,
                },
              ]}
            />
          </Card>
          <Card size="small" title="Actuals" loading={eventsQ.isLoading}>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={actuals}
              locale={{ emptyText: 'No actual events for this line' }}
              columns={[
                {
                  title: 'Document',
                  key: 'doc',
                  render: (_: unknown, r: Record<string, unknown>) => {
                    const path = (r.document_path as string) || null;
                    const label = String(r.document_number || r.source_label || r.source_type || '—');
                    return path ? <Link to={path}>{label}</Link> : label;
                  },
                },
                {
                  title: 'Amount',
                  dataIndex: 'amount',
                  align: 'right' as const,
                  width: 120,
                  render: (v: number) => formatMoney(Number(v) || 0),
                },
                {
                  title: 'Kind',
                  dataIndex: 'event_kind',
                  width: 140,
                  ellipsis: true,
                },
              ]}
            />
          </Card>
        </Space>
      </Drawer>
    </div>
  );
};

export default ProjectBudgetVsActualPage;
